import React, { useEffect, useState } from 'react'
import {
    Card,
    CardBody,
    CardHeader,
    FbCheckbox,
    CardTitle,
    FbInput,
    GridContainer,
    GridItem,
    Typography,
    FbButton,
    Icons,
} from '@fabricks/ui-components'
import { useKeycloak } from 'react-keycloak'
import { Field, FieldProps, Form, Formik, useField, useFormikContext } from 'formik'
import { Breadcrumbs, FormControl, Grid } from '@material-ui/core'
import { get as _get } from 'lodash'
import { Skeleton } from '@material-ui/lab'
import { useHistory } from 'react-router-dom'
import Swal from 'sweetalert2'
import { useApolloClient } from '@apollo/react-hooks'
import * as Yup from 'yup'
import { render } from 'react-dom'
import Navigation from '../../../constants/navigation'
import { BoxTypeResourceList, useDeleteBoxTypeMutation, useUpdateBoxTypeMutation } from '../../../api/models'
import { getContext } from '../../../services/apiService'
import { useCurrentBoxtype } from '../../../services/dataService'
import { SubmitButton } from '../Storageconfigs/EditStorageConfig'
import { showError, showWarn } from "../../../utils/sweetAlertHelper";
import { hasRole } from "../../../services/developerAccessService";

interface FormValues {
    title: string
}

const renderError = (meta: any) => {
    if (meta.error) {
        return (
            <div className="error" style={{ color: 'red' }}>
                {meta.error}
            </div>
        )
    }
    return null
}

export interface DeleteButtonProps {
    accept: () => void
    reject: () => void
}

const DeleteButton = (props: DeleteButtonProps) => {
    const { accept, reject } = props
    const { isSubmitting, values } = useFormikContext()
    const onDelete = () => {
        showWarn(`Do you really want to delete the box ${values.name}?`, `Deletion of ${values.name}`, {
            confirmButtonText: 'Yes, delete it!',
            showCancelButton: true,
            confirmButtonColor: '#d33',
        }).then((res) => {
            if (res.value) {
                return accept()
            }
            return reject()
        })
    }

    return (
        <FbButton
            disabled={isSubmitting}
            variant="contained"
            color="default"
            onClick={onDelete}
            style={{ float: 'left' }}
        >
            Delete
        </FbButton>
    )
}

export const EditBoxtypes = (props: any) => {
    const { uuid } = props.match.params
    const [keycloak] = useKeycloak()
    const history = useHistory()
    const { data, error, loading } = useCurrentBoxtype(keycloak.token, { uuid })
    const boxTypeData = _get(data, 'getBoxType.data', [])
    const [executeMutation, resultOfMutation] = useUpdateBoxTypeMutation()
    const [executeDeleteMutation, resultOfDeleteMutation] = useDeleteBoxTypeMutation()

    const BoxTypeSchema = Yup.object().shape({
        name: Yup.string()
            .min(2, 'You need to enter at least two characters')
            .required('This input field is required'),
        innerWidth: Yup.number().moreThan(0),
    })

    useEffect(() => {
        if (resultOfMutation.called && !resultOfMutation.loading && resultOfMutation.data) {
            const receivedData: BoxTypeResourceList = resultOfMutation.data.updateBoxType || {}
            const mutationError = receivedData.error
            if (mutationError) {
                Swal.fire({
                    title: `ERROR (${mutationError.code})`,
                    text: `${mutationError.message}` || 'Error while mutating the data',
                    icon: 'error',
                    confirmButtonText: 'Ok',
                })
                return
            }
            if (resultOfMutation.data) {
                Swal.fire({
                    title: 'SUCCESS',
                    text: 'Data was sucessfully updated',
                    icon: 'success',
                    confirmButtonText: 'Ok',
                }).then(result => {
                    if (result.value) {
                        history.push("/masterdata/boxTypes")
                    }
                })
            }
        }
    }, [history, resultOfMutation])

    if (resultOfMutation.called && resultOfMutation.loading) {
        return (
            <GridItem xs={12} sm={12} md={12}>
                <Card className="card">
                    <Skeleton variant="text" animation="wave" />
                    <Skeleton variant="rect" animation="wave" width={210} height={118} />
                </Card>
            </GridItem>
        )
    }

    if (loading && boxTypeData) {
        return (
            <GridItem xs={12} sm={12} md={12}>
                <Card className="card">
                    <Skeleton variant="text" animation="wave" />
                    <Skeleton variant="rect" animation="wave" width={210} height={118} />
                </Card>
            </GridItem>
        )
    }

    const onDelete = () => {
        const assignedRoles = _get(keycloak, 'tokenParsed.resource_access', {})
        if (!hasRole(assignedRoles, 'admin')) {
            return showError("You have not enough rights for this action", "")
        }

        const boxUuid = _get(boxTypeData, '0.uuid', null)
        if (boxUuid) {
            executeDeleteMutation({
                context: getContext(keycloak.token),
                variables: { id: boxUuid },
            }).then((res) => {
                const responsePayload = _get(res, 'data.deleteBoxType', {})
                if (responsePayload.success) {
                    history.goBack()
                }
            })
        }
    }

    const boxTypeDetails = boxTypeData[0]
    if (boxTypeDetails) {
        const onSubmit = (values: any) => {
            const assignedRoles = _get(keycloak, 'tokenParsed.resource_access', {})
            if (!hasRole(assignedRoles, 'admin')) {
                return showError('You have not enough rights for this action', '')
            }
            const boxTypeUpdateValue = {
                name: values.name,
                inner: {
                    width: parseInt(values.innerWidth, 10),
                    height: parseInt(values.innerHeight, 10),
                    depth: parseInt(values.innerDepth, 10),
                },
                outer: {
                    width: parseInt(values.outerWidth, 10),
                    height: parseInt(values.outerHeight, 10),
                    depth: parseInt(values.outerDepth, 10),
                },
                isActive: values.isActive,
            }
            executeMutation({
                context: getContext(keycloak.token),
                variables: { id: uuid, boxType: boxTypeUpdateValue },
            })
        }

        return (
            <GridContainer>
                <GridItem xs={12} sm={12} md={12}>
                    <Card className="card">
                        <CardHeader isStats={false} hasIcon type="plain">
                            <div style={{ margin: '2em' }}>
                                <Breadcrumbs aria-label="breadcrumb">
                                    <Grid
                                        container
                                        direction="row"
                                        alignItems="center"
                                        style={{ cursor: 'pointer' }}
                                        onClick={() => {
                                            history.goBack()
                                        }}
                                    >
                                        <Grid item>
                                            <Icons.ArrowBackIos />
                                        </Grid>
                                        <Grid item>
                                            <span>Go back</span>
                                        </Grid>
                                    </Grid>
                                </Breadcrumbs>
                            </div>
                            <div style={{ margin: '1em' }}>
                                <CardTitle align="left" title={`Edit of ${boxTypeDetails.name}`} variant="h3" />
                            </div>
                        </CardHeader>
                        <CardBody isProfile>
                            <Formik
                                initialValues={{
                                    name: boxTypeDetails.name,
                                    innerDepth: boxTypeDetails.inner.depth,
                                    innerWidth: boxTypeDetails.inner.width,
                                    innerHeight: boxTypeDetails.inner.height,
                                    outerDepth: boxTypeDetails.outer.depth,
                                    outerWidth: boxTypeDetails.outer.width,
                                    outerHeight: boxTypeDetails.outer.height,
                                    isActive: !!boxTypeDetails.isActive,
                                }}
                                validationSchema={BoxTypeSchema}
                                onSubmit={onSubmit}
                            >
                                <Form translate="yes">
                                    <GridContainer>
                                        <GridItem xs={12} sm={12} md={12}>
                                            <FormControl fullWidth>
                                                <Field
                                                    name="name"
                                                    render={({
                                                        field,
                                                        form: { isSubmitting },
                                                        meta,
                                                    }: FieldProps<FormValues>) => (
                                                        <div>
                                                            <FbInput
                                                                id="name"
                                                                label="Name"
                                                                type="text"
                                                                name="name"
                                                                className="textfield"
                                                                disabled={isSubmitting}
                                                                {...field}
                                                            />
                                                            {renderError(meta)}
                                                        </div>
                                                    )}
                                                />
                                            </FormControl>
                                        </GridItem>
                                        <GridItem xs={12} sm={6} md={6}>
                                            <FormControl fullWidth>
                                                <h3>Inner Box</h3>
                                                <Field
                                                    name="innerDepth"
                                                    render={({
                                                        field,
                                                        form: { isSubmitting },
                                                        meta,
                                                    }: FieldProps<FormValues>) => (
                                                        <div>
                                                            <FbInput
                                                                id="innerDepth"
                                                                label="Inner Depth (mm)"
                                                                type="number"
                                                                name="innerDepth"
                                                                className="textfield"
                                                                disabled={isSubmitting}
                                                                {...field}
                                                            />
                                                            {meta.touched && meta.error && (
                                                                <div className="error">{meta.error}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                />

                                                <Field
                                                    name="innerWidth"
                                                    render={({
                                                        field,
                                                        form: { isSubmitting },
                                                        meta,
                                                    }: FieldProps<FormValues>) => (
                                                        <div>
                                                            <FbInput
                                                                id="innerWidth"
                                                                label="Inner Width"
                                                                type="number"
                                                                name="innerWidth"
                                                                className="textfield"
                                                                disabled={isSubmitting}
                                                                {...field}
                                                            />
                                                            {renderError(meta)}
                                                        </div>
                                                    )}
                                                />

                                                <Field
                                                    name="innerHeight"
                                                    render={({
                                                        field,
                                                        form: { isSubmitting },
                                                        meta,
                                                    }: FieldProps<FormValues>) => (
                                                        <div>
                                                            <FbInput
                                                                id="innerHeight"
                                                                label="Inner Height"
                                                                type="number"
                                                                name="innerHeight"
                                                                className="textfield"
                                                                disabled={isSubmitting}
                                                                {...field}
                                                            />
                                                            {meta.touched && meta.error && (
                                                                <div className="error">{meta.error}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                />
                                            </FormControl>
                                        </GridItem>
                                        <GridItem xs={12} sm={6} md={6}>
                                            <FormControl fullWidth>
                                                <h3>Outer Box</h3>
                                                <Field
                                                    name="outerDepth"
                                                    render={({
                                                        field,
                                                        form: { isSubmitting },
                                                        meta,
                                                    }: FieldProps<FormValues>) => (
                                                        <div>
                                                            <FbInput
                                                                id="outerDepth"
                                                                label="Outer Depth"
                                                                type="number"
                                                                name="outerDepth"
                                                                className="textfield"
                                                                disabled={isSubmitting}
                                                                {...field}
                                                            />
                                                            {meta.touched && meta.error && (
                                                                <div className="error">{meta.error}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                />

                                                <Field
                                                    name="outerWidth"
                                                    render={({
                                                        field,
                                                        form: { isSubmitting },
                                                        meta,
                                                    }: FieldProps<FormValues>) => (
                                                        <div>
                                                            <FbInput
                                                                id="outerWidth"
                                                                label="Outer Width"
                                                                type="number"
                                                                name="outerrWidth"
                                                                className="textfield"
                                                                disabled={isSubmitting}
                                                                {...field}
                                                            />
                                                            {meta.touched && meta.error && (
                                                                <div className="error">{meta.error}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                />

                                                <Field
                                                    name="outerHeight"
                                                    render={({
                                                        field,
                                                        form: { isSubmitting },
                                                        meta,
                                                    }: FieldProps<FormValues>) => (
                                                        <div>
                                                            <FbInput
                                                                id="outerHeight"
                                                                label="Outer Height"
                                                                type="number"
                                                                name="outerHeight"
                                                                className="textfield"
                                                                disabled={isSubmitting}
                                                                {...field}
                                                            />
                                                            {meta.touched && meta.error && (
                                                                <div className="error">{meta.error}</div>
                                                            )}
                                                        </div>
                                                    )}
                                                />
                                            </FormControl>
                                        </GridItem>

                                        <GridItem xs={12} sm={12} md={12}>
                                            <Field
                                                name="isActive"
                                                render={({
                                                    field,
                                                    form: { isSubmitting },
                                                    meta,
                                                }: FieldProps<FormValues>) => (
                                                    <div>
                                                        <FbCheckbox
                                                            label="Active"
                                                            id="isActive"
                                                            checked={!!field.value}
                                                            disabled={isSubmitting}
                                                            {...field}
                                                        />
                                                        {meta.touched && meta.error && (
                                                            <div className="error">{meta.error}</div>
                                                        )}
                                                    </div>
                                                )}
                                            />
                                        </GridItem>
                                        <GridItem xs={12} sm={12} md={12}>
                                            <SubmitButton />
                                            <DeleteButton accept={onDelete} reject={() => {}} />
                                        </GridItem>
                                    </GridContainer>
                                </Form>
                            </Formik>
                        </CardBody>
                    </Card>
                </GridItem>
            </GridContainer>
        )
    }
    if (resultOfMutation.loading) {
        return (
            <GridItem xs={12} sm={12} md={12}>
                <Card className="card">
                    <Skeleton variant="text" animation="wave" />
                    <Skeleton variant="rect" animation="wave" width={210} height={118} />
                </Card>
            </GridItem>
        )
    }
    return (
        <GridItem xs={12} sm={12} md={12}>
            <Card className="card">Currently no data</Card>
        </GridItem>
    )
}

export default EditBoxtypes
